import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, AlertTriangle, CheckCircle, XCircle, Trash2, RefreshCw, Edit2, Eye, Download, Merge } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminDatabaseCheck = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [activeTab, setActiveTab] = useState('ref_frs');
  const [editingRefFrs, setEditingRefFrs] = useState(null);
  const [newRefFrs, setNewRefFrs] = useState('');
  const [viewingProduct, setViewingProduct] = useState(null);
  const [productDetails, setProductDetails] = useState(null);

  const fetchConsistencyCheck = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/database/check-consistency`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(response.data);
    } catch (error) {
      console.error('Error checking database:', error);
      alert('Erreur lors de la vérification de la base de données');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchConsistencyCheck();
  }, []);

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev => 
      prev.includes(productId) 
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAllInGroup = (products) => {
    const productIds = products.map(p => p.id);
    const allSelected = productIds.every(id => selectedProducts.includes(id));
    
    if (allSelected) {
      setSelectedProducts(prev => prev.filter(id => !productIds.includes(id)));
    } else {
      setSelectedProducts(prev => [...new Set([...prev, ...productIds])]);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedProducts.length === 0) {
      alert('Aucun produit sélectionné');
      return;
    }

    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedProducts.length} produit(s) ?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API}/admin/database/products/bulk`, {
        headers: { Authorization: `Bearer ${token}` },
        data: selectedProducts
      });
      
      alert(`${selectedProducts.length} produit(s) supprimé(s)`);
      setSelectedProducts([]);
      fetchConsistencyCheck();
    } catch (error) {
      console.error('Error deleting products:', error);
      alert('Erreur lors de la suppression des produits');
    }
  };

  const handleUpdateRefFrs = async (productId) => {
    if (!newRefFrs.trim()) {
      alert('Le ref_frs ne peut pas être vide');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.put(
        `${API}/admin/database/products/${productId}/ref-frs`,
        null,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { ref_frs: newRefFrs.trim() }
        }
      );
      
      alert('ref_frs mis à jour avec succès');
      setEditingRefFrs(null);
      setNewRefFrs('');
      fetchConsistencyCheck();
    } catch (error) {
      console.error('Error updating ref_frs:', error);
      alert('Erreur lors de la mise à jour du ref_frs');
    }
  };

  const handleMergeDuplicates = async (keepProductId, allProducts) => {
    const deleteIds = allProducts
      .filter(p => p.id !== keepProductId)
      .map(p => p.id);

    if (deleteIds.length === 0) {
      alert('Aucun produit à supprimer');
      return;
    }

    if (!window.confirm(`Garder le produit sélectionné et supprimer ${deleteIds.length} autre(s) produit(s) ?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      await axios.post(
        `${API}/admin/database/products/merge-duplicates`,
        {
          keep_product_id: keepProductId,
          delete_product_ids: deleteIds
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      alert('Fusion réussie');
      fetchConsistencyCheck();
    } catch (error) {
      console.error('Error merging duplicates:', error);
      alert('Erreur lors de la fusion des doublons');
    }
  };

  const handleViewProduct = async (productId) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API}/admin/database/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProductDetails(response.data);
      setViewingProduct(productId);
    } catch (error) {
      console.error('Error fetching product details:', error);
      alert('Erreur lors du chargement des détails du produit');
    }
  };

  const handleExportToCSV = () => {
    if (!results) return;

    let csvContent = 'data:text/csv;charset=utf-8,';
    
    // Headers
    csvContent += 'Type,Ref_FRS,Gamme,Order,Code1,Code2,Code3,Supplier,Category,Product_ID\n';
    
    // Duplicates ref_frs
    results.duplicates_ref_frs.forEach(group => {
      group.products.forEach(product => {
        csvContent += `Duplicate_RefFRS,${product.ref_frs},${product.gamme || ''},${product.order || ''},${product.code1 || ''},${product.code2 || ''},${product.code3 || ''},${product.supplier || ''},${product.category || ''},${product.id}\n`;
      });
    });
    
    // Duplicates ref_frs + gamme
    results.duplicates_ref_frs_gamme.forEach(group => {
      group.products.forEach(product => {
        csvContent += `Duplicate_RefFRS_Gamme,${product.ref_frs},${product.gamme || ''},${product.order || ''},${product.code1 || ''},${product.code2 || ''},${product.code3 || ''},${product.supplier || ''},${product.category || ''},${product.id}\n`;
      });
    });
    
    // Missing ref_frs
    results.missing_ref_frs.forEach(product => {
      csvContent += `Missing_RefFRS,,${product.gamme || ''},${product.order || ''},${product.code1 || ''},${product.code2 || ''},${product.code3 || ''},${product.supplier || ''},${product.category || ''},${product.id}\n`;
    });
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `database_check_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading && !results) {
    return (
      <div className="min-h-screen bg-gray-50 p-8">
        <div className="flex items-center justify-center">
          <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          <span className="ml-3 text-lg">Analyse de la base de données...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="p-2 hover:bg-gray-200 rounded-full"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-3xl font-bold">Vérification de la base de données</h1>
          </div>
          <button
            onClick={fetchConsistencyCheck}
            disabled={loading}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            <span>Actualiser</span>
          </button>
        </div>

        {/* Summary Cards */}
        {results && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Total produits</p>
                  <p className="text-2xl font-bold">{results.summary.total_products}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Doublons ref_frs</p>
                  <p className="text-2xl font-bold text-orange-600">{results.summary.duplicate_ref_frs_count}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-orange-500" />
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">Doublons ref_frs+gamme</p>
                  <p className="text-2xl font-bold text-orange-600">{results.summary.duplicate_ref_frs_gamme_count}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-orange-500" />
              </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-500 text-sm">ref_frs manquant</p>
                  <p className="text-2xl font-bold text-red-600">{results.summary.missing_ref_frs_count}</p>
                </div>
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
            </div>
          </div>
        )}

        {/* Actions Bar */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 flex items-center justify-between">
          <button
            onClick={handleExportToCSV}
            disabled={!results}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            <span>Exporter en CSV</span>
          </button>
          
          {selectedProducts.length > 0 && (
            <div className="flex items-center space-x-3">
              <span className="text-gray-700 font-medium">
                {selectedProducts.length} produit(s) sélectionné(s)
              </span>
              <button
                onClick={handleDeleteSelected}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                <Trash2 className="w-4 h-4" />
                <span>Supprimer la sélection</span>
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex">
              <button
                onClick={() => setActiveTab('ref_frs')}
                className={`px-6 py-3 font-medium ${
                  activeTab === 'ref_frs'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Doublons ref_frs ({results?.duplicates_ref_frs.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('ref_frs_gamme')}
                className={`px-6 py-3 font-medium ${
                  activeTab === 'ref_frs_gamme'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Doublons ref_frs+gamme ({results?.duplicates_ref_frs_gamme.length || 0})
              </button>
              <button
                onClick={() => setActiveTab('missing')}
                className={`px-6 py-3 font-medium ${
                  activeTab === 'missing'
                    ? 'border-b-2 border-blue-600 text-blue-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                ref_frs manquant ({results?.missing_ref_frs.length || 0})
              </button>
            </nav>
          </div>

          <div className="p-6">
            {/* Duplicates ref_frs */}
            {activeTab === 'ref_frs' && results && (
              <div className="space-y-6">
                {results.duplicates_ref_frs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
                    <p>Aucun doublon détecté pour ref_frs</p>
                  </div>
                ) : (
                  results.duplicates_ref_frs.map((group, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-lg">
                          ref_frs: <span className="text-orange-600">{group.ref_frs}</span>
                          <span className="ml-3 text-sm text-gray-500">
                            ({group.products.length} produits)
                          </span>
                        </h3>
                        <button
                          onClick={() => handleSelectAllInGroup(group.products)}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          {group.products.every(p => selectedProducts.includes(p.id))
                            ? 'Désélectionner tout'
                            : 'Sélectionner tout'}
                        </button>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                <input type="checkbox" className="rounded" />
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">ORDER</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">CODE1</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">CODE2</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">CODE3</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">GAMME</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">SUPPLIER</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">ACTIONS</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {group.products.map((product) => (
                              <tr key={product.id} className="hover:bg-gray-50">
                                <td className="px-3 py-2">
                                  <input
                                    type="checkbox"
                                    checked={selectedProducts.includes(product.id)}
                                    onChange={() => handleSelectProduct(product.id)}
                                    className="rounded"
                                  />
                                </td>
                                <td className="px-3 py-2 text-sm">{product.order}</td>
                                <td className="px-3 py-2 text-sm">{product.code1}</td>
                                <td className="px-3 py-2 text-sm">{product.code2}</td>
                                <td className="px-3 py-2 text-sm">{product.code3}</td>
                                <td className="px-3 py-2 text-sm">{product.gamme}</td>
                                <td className="px-3 py-2 text-sm">{product.supplier}</td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={() => handleViewProduct(product.id)}
                                      className="text-blue-600 hover:text-blue-800"
                                      title="Voir les détails"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleMergeDuplicates(product.id, group.products)}
                                      className="text-green-600 hover:text-green-800 text-xs font-medium px-2 py-1 border border-green-600 rounded"
                                      title="Garder celui-ci"
                                    >
                                      Garder
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Duplicates ref_frs + gamme */}
            {activeTab === 'ref_frs_gamme' && results && (
              <div className="space-y-6">
                {results.duplicates_ref_frs_gamme.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
                    <p>Aucun doublon détecté pour ref_frs + gamme</p>
                  </div>
                ) : (
                  results.duplicates_ref_frs_gamme.map((group, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-lg">
                          <span className="text-orange-600">{group.key}</span>
                          <span className="ml-3 text-sm text-gray-500">
                            ({group.products.length} produits)
                          </span>
                        </h3>
                        <button
                          onClick={() => handleSelectAllInGroup(group.products)}
                          className="text-sm text-blue-600 hover:text-blue-800"
                        >
                          {group.products.every(p => selectedProducts.includes(p.id))
                            ? 'Désélectionner tout'
                            : 'Sélectionner tout'}
                        </button>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                                <input type="checkbox" className="rounded" />
                              </th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">ORDER</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">REF-FRS</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">CODE1</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">CODE2</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">CODE3</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">SUPPLIER</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">ACTIONS</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {group.products.map((product) => (
                              <tr key={product.id} className="hover:bg-gray-50">
                                <td className="px-3 py-2">
                                  <input
                                    type="checkbox"
                                    checked={selectedProducts.includes(product.id)}
                                    onChange={() => handleSelectProduct(product.id)}
                                    className="rounded"
                                  />
                                </td>
                                <td className="px-3 py-2 text-sm">{product.order}</td>
                                <td className="px-3 py-2 text-sm">{product.ref_frs}</td>
                                <td className="px-3 py-2 text-sm">{product.code1}</td>
                                <td className="px-3 py-2 text-sm">{product.code2}</td>
                                <td className="px-3 py-2 text-sm">{product.code3}</td>
                                <td className="px-3 py-2 text-sm">{product.supplier}</td>
                                <td className="px-3 py-2">
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={() => handleViewProduct(product.id)}
                                      className="text-blue-600 hover:text-blue-800"
                                      title="Voir les détails"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => handleMergeDuplicates(product.id, group.products)}
                                      className="text-green-600 hover:text-green-800 text-xs font-medium px-2 py-1 border border-green-600 rounded"
                                      title="Garder celui-ci"
                                    >
                                      Garder
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Missing ref_frs */}
            {activeTab === 'missing' && results && (
              <div>
                {results.missing_ref_frs.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
                    <p>Aucun produit avec ref_frs manquant</p>
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-lg">
                        Produits sans ref_frs
                        <span className="ml-3 text-sm text-gray-500">
                          ({results.missing_ref_frs.length} produits)
                        </span>
                      </h3>
                      <button
                        onClick={() => handleSelectAllInGroup(results.missing_ref_frs)}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        {results.missing_ref_frs.every(p => selectedProducts.includes(p.id))
                          ? 'Désélectionner tout'
                          : 'Sélectionner tout'}
                      </button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                              <input type="checkbox" className="rounded" />
                            </th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">ORDER</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">CODE1</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">CODE2</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">CODE3</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">SUPPLIER</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">CATEGORY</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">ACTIONS</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {results.missing_ref_frs.map((product) => (
                            <tr key={product.id} className="hover:bg-gray-50">
                              <td className="px-3 py-2">
                                <input
                                  type="checkbox"
                                  checked={selectedProducts.includes(product.id)}
                                  onChange={() => handleSelectProduct(product.id)}
                                  className="rounded"
                                />
                              </td>
                              <td className="px-3 py-2 text-sm">{product.order}</td>
                              <td className="px-3 py-2 text-sm">{product.code1}</td>
                              <td className="px-3 py-2 text-sm">{product.code2}</td>
                              <td className="px-3 py-2 text-sm">{product.code3}</td>
                              <td className="px-3 py-2 text-sm">{product.supplier}</td>
                              <td className="px-3 py-2 text-sm">{product.category}</td>
                              <td className="px-3 py-2">
                                {editingRefFrs === product.id ? (
                                  <div className="flex items-center space-x-2">
                                    <input
                                      type="text"
                                      value={newRefFrs}
                                      onChange={(e) => setNewRefFrs(e.target.value)}
                                      placeholder="ref_frs"
                                      className="border border-gray-300 rounded px-2 py-1 text-sm w-32"
                                      autoFocus
                                    />
                                    <button
                                      onClick={() => handleUpdateRefFrs(product.id)}
                                      className="text-green-600 hover:text-green-800 text-xs font-medium"
                                    >
                                      ✓
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingRefFrs(null);
                                        setNewRefFrs('');
                                      }}
                                      className="text-red-600 hover:text-red-800 text-xs font-medium"
                                    >
                                      ✗
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center space-x-2">
                                    <button
                                      onClick={() => handleViewProduct(product.id)}
                                      className="text-blue-600 hover:text-blue-800"
                                      title="Voir les détails"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </button>
                                    <button
                                      onClick={() => {
                                        setEditingRefFrs(product.id);
                                        setNewRefFrs('');
                                      }}
                                      className="text-orange-600 hover:text-orange-800"
                                      title="Ajouter ref_frs"
                                    >
                                      <Edit2 className="w-4 h-4" />
                                    </button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Product Details Modal */}
        {viewingProduct && productDetails && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold">Détails du produit</h2>
                  <button
                    onClick={() => {
                      setViewingProduct(null);
                      setProductDetails(null);
                    }}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(productDetails)
                    .filter(([key]) => key !== '_id' && key !== 'id')
                    .map(([key, value]) => (
                      <div key={key} className="border-b border-gray-200 pb-2">
                        <div className="text-sm text-gray-500 font-medium">{key}</div>
                        <div className="text-sm text-gray-900 break-words">
                          {value !== null && value !== undefined && value !== '' 
                            ? String(value) 
                            : <span className="text-gray-400 italic">Non défini</span>
                          }
                        </div>
                      </div>
                    ))}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => {
                      setViewingProduct(null);
                      setProductDetails(null);
                    }}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDatabaseCheck;
